import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import { IUser, Maybe } from '../../types/graphql'
import Review from './Review'

const ReviewsModal = dynamic(() => import('./ReviewsModal'))

const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800'

type IProps = {
  user?: Maybe<IUser> | undefined
  /** Forwarded to every host reply — see `Review`'s `anonymizeOwner`. */
  anonymizeOwner?: boolean
}

/**
 * Editorial reviews section — public listing page (`Details`, host anonymized)
 * and the public host/team profile (`UserProfile`). Left rail = eyebrow +
 * Fraunces average + summary; below, a two-column grid of hairline-topped
 * review cells. SSR'd on both pages: every string is ONE `t()` node (the old
 * "reseña{s} verificada{s}" run was five adjacent text nodes — Trigger H).
 */
function Reviews({ user, anonymizeOwner }: IProps) {
  const { t } = useTranslation('common')
  const [isOpen, setIsOpen] = useState(false)
  const { reviews } = user ?? {}

  const { total, hits, avgRating } = reviews ?? {}
  if (!total || !hits || hits.length === 0) return null

  // `toFixed` is deterministic (no ICU) — safe on the SSG listing page. Same
  // one-decimal format as the profile masthead's rating.
  const ratingLabel = avgRating != null ? avgRating.toFixed(1) : ''

  return (
    <section className="border-t border-ink-100/15 py-12 md:py-24">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-12">
        <div className="md:col-span-5">
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {t('REVIEWS.EYEBROW')}
          </p>
          <h2 className="mt-3 font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
            <span className="font-display italic">{ratingLabel}</span>
            <span className="ml-2 text-ink-200">/5</span>
          </h2>
          <p className="mt-3 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
            {t('REVIEWS.SUMMARY', { count: total })}
          </p>
        </div>
        <div className="md:col-span-7 md:self-end">
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className={`group hidden min-h-11 items-center gap-2 rounded-sm text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:text-blue-800 md:inline-flex ${FOCUS_RING}`}
          >
            <span className="underline decoration-blue-500/40 underline-offset-[6px] transition-colors group-hover:decoration-blue-800">
              {t('REVIEWS.SEE_ALL')}
            </span>
            <span
              aria-hidden="true"
              className="transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none"
            >
              →
            </span>
          </button>
        </div>
      </div>

      <ul className="mt-8 grid grid-cols-1 md:mt-12 md:grid-cols-2 md:gap-x-12">
        {hits.map((review, index) => (
          <li key={index} className="min-w-0">
            <Review
              review={review}
              owner={user}
              anonymizeOwner={anonymizeOwner}
            />
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`mt-4 inline-flex min-h-11 w-full items-center justify-center border border-ink-100/40 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:border-blue-800 hover:text-blue-800 active:bg-cream-300 md:hidden ${FOCUS_RING}`}
      >
        {t('REVIEWS.SEE_ALL')}
      </button>

      {user && isOpen && (
        <ReviewsModal
          user={user}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          anonymizeOwner={anonymizeOwner}
        />
      )}
    </section>
  )
}

export default Reviews
